import { Box, Button, CircularProgress, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useUpdateProfile } from '../hooks/UseUpdateProfile';
import UploadImage from '../images/icons/upload-image.png';
import { useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from '../context/AuthContext';
import { useWaits } from '../hooks/UseWait';
import { useConstants } from '../hooks/UseConstants';
import Fetch from '../services/Fetch';
import { buildProfileFormData } from '../helper/ProfileFormData';

const resolveDegreeLevel = (degree) => {
    if (!degree) return '';

    const explicitLevel = String(degree.level || '').toLowerCase().trim();
    if (explicitLevel) return explicitLevel;

    const degreeLabel = `${degree.name_en || ''} ${degree.name_ar || ''}`.toLowerCase();

    if (/bachelor|master|phd|diploma|university|بكالوريوس|ماجستير|دكتوراه|دبلوم|جامعي/.test(degreeLabel)) {
        return 'university';
    }

    if (/school|grade|primary|secondary|بكلوريا|ثانوي|ابتدائي|إعدادي|مدرسي/.test(degreeLabel)) {
        return 'school';
    }

    return '';
};

function UpdateProfile({ onClickCancel, setSnackBar }) {
    const { host } = useConstants();
    const { user, refreshUser } = useContext(AuthContext);
    const {
        firstName, setFirstName, middleName, setMiddleName, lastName, setLastName,
        code, setCode, phoneNumber, setPhoneNumber, image, setImage, birthDate, setBirthDate,
        majorId, setMajorId, academicDegreeId, setAcademicDegreeId,
    } = useUpdateProfile();
    const { sendWait, setSendWait } = useWaits();
    const theme = useTheme();
    const [degrees, setDegrees] = useState([]);
    const [majors, setMajors] = useState([]);

    const handleChange = (value, country) => {
        const numberWithoutCode = value.replace(country.dialCode, '');
        setCode(country.dialCode);
        setPhoneNumber(numberWithoutCode);
    };

    const setInputs = () => {
        setFirstName(user.first_name || '');
        setMiddleName(user.middle_name || '');
        setLastName(user.last_name || '');
        setCode(user.phone_code || user.code || '');
        setPhoneNumber(user.phone || '');
        setBirthDate(user.birth_date || '');
        setMajorId(user.major?.id ? String(user.major.id) : '');
        setAcademicDegreeId(user.academic_degree?.id ? String(user.academic_degree.id) : '');
    };

    const selectedDegree = useMemo(
        () => degrees.find((degree) => String(degree.id) === String(academicDegreeId)),
        [degrees, academicDegreeId]
    );

    const degreeLevel = resolveDegreeLevel(selectedDegree);

    const filteredMajors = useMemo(() => (
        majors.filter((major) => {
            if (!degreeLevel || !major.level) return true;
            return major.level === degreeLevel;
        })
    ), [majors, degreeLevel]);

    const updateProfile = async () => {
        setSendWait(true);
        const formData = buildProfileFormData({
            firstName,
            middleName,
            lastName,
            image,
            phoneCode: code,
            phoneNumber,
            email: user.email,
            birthDate,
            majorId,
            academicDegreeId,
            language: user.language,
        });

        const result = await Fetch(host + '/account/update-profile', 'POST', formData);

        if (result.status === 200) {
            await refreshUser();
            setSnackBar('success', 'تم التحديث بنجاح');
            onClickCancel();
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || 'تعذر التحديث');
        }

        setSendWait(false);
    };

    useEffect(() => {
        if (user) setInputs();
    }, [user]);

    useEffect(() => {
        const loadAcademicOptions = async () => {
            const [degreesResult, majorsResult] = await Promise.all([
                Fetch(host + '/academic-degrees?per_page=100', 'GET'),
                Fetch(host + '/majors?per_page=200', 'GET'),
            ]);

            if (degreesResult.status === 200) {
                setDegrees(degreesResult.data?.data || []);
            }

            if (majorsResult.status === 200) {
                setMajors(majorsResult.data?.data || []);
            }
        };

        loadAcademicOptions();
    }, [host]);

    return (
        <Box sx={{ background: theme.palette.background.paper }} className="w-1/2 max-h-[90vh] rounded-xl py-3 px-5 overflow-y-auto max-sm:w-5/6">
            <Box className="w-20 h-20 rounded-full mx-auto my-5 flex justify-center items-center" sx={{ background: '#E8EEFD' }}>
                <EditOutlinedIcon className="text-blue-500 !text-5xl" />
            </Box>
            <Typography variant="h5" fontWeight={800} className="text-center">تحديث الملف الشخصي</Typography>
            <Typography variant="body1" className="text-center !mt-3 text-gray-700">حدّث معلوماتك الأساسية</Typography>
            <TextField className="w-full !mt-5" label="الاسم الأول" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <TextField className="w-full !mt-5" label="اسم الأب" value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
            <TextField className="w-full !mt-5" label="اسم العائلة" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            <TextField type="date" className="w-full !mt-5" label="تاريخ الميلاد" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField
                select
                className="w-full !mt-5"
                label="المرحلة الدراسية"
                value={academicDegreeId}
                onChange={(e) => {
                    setAcademicDegreeId(e.target.value);
                    setMajorId('');
                }}
            >
                {degrees.map((degree) => (
                    <MenuItem key={degree.id} value={degree.id}>
                        {degree.name_ar || degree.name_en}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                select
                className="w-full !mt-5"
                label="التخصص"
                value={majorId}
                onChange={(e) => setMajorId(e.target.value)}
                helperText={academicDegreeId ? 'تمت فلترة التخصصات بحسب المرحلة المختارة.' : 'يمكنك اختيار التخصص مباشرة.'}
            >
                {filteredMajors.map((major) => (
                    <MenuItem key={major.id} value={major.id}>
                        {major.name_ar || major.name_en}
                    </MenuItem>
                ))}
            </TextField>
            <Box dir="ltr" className="w-full h-14 mt-5">
                <PhoneInput
                    country="sy"
                    value={code + phoneNumber}
                    containerStyle={{ width: '100%' }}
                    inputStyle={{ width: '100%', height: '100%' }}
                    onChange={handleChange}
                />
            </Box>
            <Box className="relative w-full h-32 bg-gray-200 rounded-xl mt-5 flex flex-col items-center justify-center cursor-pointer">
                <img src={UploadImage} alt="" />
                <Typography variant="body1" className="text-gray-700">إضافة صورة</Typography>
                <input type="file" accept="image/*" className="w-full h-full opacity-0 absolute cursor-pointer" onChange={(e) => setImage(e.target.files?.[0])} />
            </Box>
            <Box className="py-3 flex justify-between gap-2 max-sm:flex-col">
                <Button onClick={updateProfile} variant="contained" className="flex-1 !py-3" disabled={sendWait}>
                    {sendWait ? <CircularProgress size={20} color="inherit" /> : <>حفظ<SaveOutlinedIcon className="mr-1" /></>}
                </Button>
                <Button onClick={() => { setInputs(); onClickCancel(); }} variant="contained" color="inherit" className="flex-1 !py-3 !text-gray-600">
                    إلغاء
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateProfile;
